import { RequirementType } from "@wingspanhq/payments/dist/interfaces";
import { QUERY_BACKGROUND_CHECKS_REQUEST } from "../../backgroundChecks/queries/useQueryBackgroundChecksRequest";
import { getBackgroundChecksRequest } from "../../../services/backgroundChecks/getBackgroundChecksRequest";
import { useWSQueries } from "@ws-react-query";
import { QUERY_SHARED_FILE_SETTINGS } from "../../sharedFile/keys";
import { getSharedFileSettings } from "../../../services/sharedFile/getSharedFileSettings";
import { QUERY_SIGNED_DOCUMENT_TEMPLATE } from "../../signedDocuments/keys";
import { getSignedDocumentTemplate } from "../../../services/signedDocuments";
import { ISharedFileRequestSettingsResponse } from "@wingspanhq/shared-files/dist/lib/interfaces";
import { IBackgroundCheckRequestResponse } from "@wingspanhq/background-checks/dist/lib/interfaces";
import { ISignedDocumentTemplateResponse } from "@wingspanhq/signed-documents/dist/lib/interfaces";
import { QUERY_MONITOR_SETTINGS_ITEM } from "../../monitorSettings/keys";
import { getMonitorSettingsItem } from "../../../services/api/insurance/monitorSettings/getMonitorSettingsItem";
import { IInsuranceCoverageMonitorSettings } from "../../../services/api/insurance/monitorSettings/types";

type RequirementDefinitionBase = {
  dataSourceId?: string;
  requirementType: RequirementType;
};

export type GetRequirementDefinitionDataSourceSettingsParams = {
  requirementDefinitions: RequirementDefinitionBase[];
  fetch: "all" | "contextType";
  enabled?: boolean;
};

export type RequirementDefinitionDataSourceSettingsItem =
  | ISharedFileRequestSettingsResponse
  | IBackgroundCheckRequestResponse
  | ISignedDocumentTemplateResponse
  | IInsuranceCoverageMonitorSettings;

export const useQueryRequirementDefinitionsSettings = (
  params: GetRequirementDefinitionDataSourceSettingsParams
) => {
  const {
    requirementDefinitions = [],
    enabled = true,
    fetch = "contextType"
  } = params;

  const configs: Record<
    RequirementType,
    {
      queryKey: (requirement: RequirementDefinitionBase) => any[] | null;
      queryFn: (requirement: RequirementDefinitionBase) => () => Promise<any>;
    }
  > = {
    [RequirementType.BackgroundCheck]: {
      queryKey: requirement => [
        QUERY_BACKGROUND_CHECKS_REQUEST,
        requirement.dataSourceId
      ],
      queryFn: requirement => () =>
        getBackgroundChecksRequest(requirement.dataSourceId!)
    },
    [RequirementType.SharedFile]: {
      queryKey: requirement => [
        QUERY_SHARED_FILE_SETTINGS,
        requirement.dataSourceId
      ],
      queryFn: requirement => () =>
        getSharedFileSettings(requirement.dataSourceId!)
    },
    [RequirementType.Signature]: {
      queryKey: requirement => [
        QUERY_SIGNED_DOCUMENT_TEMPLATE,
        requirement.dataSourceId
      ],
      queryFn: requirement => () =>
        getSignedDocumentTemplate(requirement.dataSourceId!)
    },
    [RequirementType.IdentityVerification]: {
      queryKey: requirement => null,
      queryFn: requirement => () => Promise.resolve(null)
    },
    [RequirementType.InsuranceCoverage]: {
      queryKey: requirement => [
        QUERY_MONITOR_SETTINGS_ITEM,
        requirement.dataSourceId
      ],
      queryFn: requirement => () =>
        getMonitorSettingsItem(requirement.dataSourceId!)
    },
    [RequirementType.Registration]: {
      queryKey: requirement => null,
      queryFn: requirement => () => Promise.resolve(null)
    },
    [RequirementType.TaxVerification]: {
      queryKey: requirement => null,
      queryFn: requirement => () => Promise.resolve(null)
    },
    [RequirementType.PayoutMethod]: {
      queryKey: requirement => null,
      queryFn: requirement => () => Promise.resolve(null)
    }
  };

  const queryConfigs = requirementDefinitions
    .filter(requirementDefinition => requirementDefinition.dataSourceId)
    .filter(requirementDefinition =>
      fetch === "all"
        ? requirementDefinition.dataSourceId
        : fetch === "contextType"
        ? requirementDefinition.requirementType ===
            RequirementType.SharedFile ||
          requirementDefinition.requirementType ===
            RequirementType.InsuranceCoverage
        : false
    )
    .filter(requirement =>
      configs[requirement.requirementType].queryKey(requirement)
    )
    .map(requirement => ({
      dataSourceId: requirement.dataSourceId,
      queryKey: configs[requirement.requirementType].queryKey(requirement),
      queryFn: configs[requirement.requirementType].queryFn(requirement),
      retry: false,
      enabled
    })) as any;

  const queries = useWSQueries({ queries: queryConfigs });

  const isLoading = queries.some(query => query.isLoading);
  const isFetching = queries.some(query => query.isFetching);
  const isFetched = queries.every(query => query.isFetched);

  const list = queries
    .map((query, index) => query.data)
    .filter(Boolean) as Array<RequirementDefinitionDataSourceSettingsItem>;

  return {
    isLoading,
    isFetching,
    isFetched,
    list,
    data: list.reduce((acc, requirementDefinitionSource, index) => {
      return {
        ...acc,
        [queryConfigs[index].dataSourceId]: requirementDefinitionSource
      };
    }, {} as Record<string, RequirementDefinitionDataSourceSettingsItem>)
  };
};
